import React, { useCallback, useEffect, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment } from '@fortawesome/pro-light-svg-icons';
import {
  Card,
  ErrorBanner,
  EmptyState,
  RatingStars,
  formatDate,
} from './shared';
import { FeedbackDetail, getFeedbackList } from './api';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const FeedbackTab: React.FC = () => {
  const [rows, setRows] = useState<FeedbackDetail[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFeedbackList({
        limit: perPage,
        offset: page * perPage,
      });
      setRows(res.items);
      setTotal(res.total);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err?.message || 'Failed to load feedback.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Card>
      <TrueImpactText variant="heading-subheading">Feedback</TrueImpactText>
      <TrueImpactText
        variant="body-small"
        color="grayDark"
        style={{ display: 'block', marginTop: 4, marginBottom: 12 }}
      >
        All submitted ratings with the originating query and caller info,
        newest first.
      </TrueImpactText>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {loading && rows.length === 0 ? (
        <TrueImpactLoading />
      ) : rows.length === 0 ? (
        <EmptyState>
          <div className="icon">
            <FontAwesomeIcon icon={faComment} />
          </div>
          <TrueImpactText>No feedback submitted yet.</TrueImpactText>
        </EmptyState>
      ) : (
        <>
          <TableContainer
            style={{ opacity: loading ? 0.6 : 1, transition: '0.2s' }}
          >
            <MuiTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rating</TableCell>
                  <TableCell>Query</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Caller</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(f => (
                  <TableRow key={f.id} hover>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      <RatingStars value={f.rating} size={13} />
                    </TableCell>
                    <TableCell style={{ maxWidth: 260 }}>
                      <Tooltip title={f.query_text} placement="top">
                        <span
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {f.query_text}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell style={{ maxWidth: 300 }}>
                      {f.comment || '—'}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(f.created_at)}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      <Tooltip
                        placement="top"
                        title={
                          <>
                            <div>IP: {f.caller_ip || '—'}</div>
                            <div>Fwd: {f.forwarded_for || '—'}</div>
                            <div>UA: {f.user_agent || '—'}</div>
                            <div>Origin: {f.origin || '—'}</div>
                            <div>Referer: {f.referer || '—'}</div>
                          </>
                        }
                      >
                        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                          {f.caller_ip || '—'}
                        </span>
                      </Tooltip>
                    </TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={perPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onRowsPerPageChange={e => {
              setPage(0);
              setPerPage(parseInt(e.target.value, 10));
            }}
          />
        </>
      )}
    </Card>
  );
};

export default FeedbackTab;
